import { HttpStatusCode } from '@angular/common/http';
import { inject, Injector, Service } from '@angular/core';
import type {
  OnyxComponentInputs,
  OnyxDropdownOptionsSourceResult,
  OnyxFilter,
  OnyxHttpErrorResponse,
  OnyxOption,
  OnyxPaginated,
  OnyxPagination,
  OnyxPhone,
} from '@onyx/angular';
import { OnyxDatePipe, OnyxNamePipe, OnyxPhonePipe } from '@onyx/angular';
import { omit } from 'lodash';
import type { Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  from,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import { IconTextTooltipComponent } from '../../../../common/components/tooltips/icon-text-tooltip/icon-text-tooltip.component';
import type { UnavailabilityForm } from '../../../../common/components/unavailabilities/unavailability-modal/unavailability-modal.component';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ConflictHelper } from '../../../../common/helpers/conflict.helper';
import { FilterHelper } from '../../../../common/helpers/filter.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { Unavailability } from '../../../../common/interfaces/common/unavailability';
import type { BatchFileUpload } from '../../../../common/interfaces/utilities/batch-file-upload';
import { ApiService } from '../../../../common/services/api.service';
import { StorageService } from '../../../../common/services/storage.service';
import type { ImportProgress } from '../../../common/interfaces/import-progress';
import { DelegationsService } from '../../../delegations/common/services/delegations.service';
import { FleetService } from '../../../fleet/common/services/fleet.service';
import type { DriversImportData } from '../constants/drivers-import-schema';
import type { DriverFormDto } from '../helpers/driver-form.helper';
import type { DriverSort } from '../enums/driver-sort';
import type { Driver } from '../interfaces/driver';

export interface DriversFilters {
  filters?: OnyxFilter[];
  query?: string;
  sort?: DriverSort;
}

@Service()
export class DriversService extends ApiService {
  private readonly authStore = inject(AuthStore);
  private readonly storageService = inject(StorageService);
  private readonly injector = inject(Injector);
  private readonly phonePipe = inject(OnyxPhonePipe);
  private readonly conflictHelper = inject(ConflictHelper);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly namePipe = inject(OnyxNamePipe);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listDrivers(
    params: OnyxPagination & DriversFilters,
  ): Observable<OnyxPaginated<Driver>> {
    return this.get('/drivers', {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.filters && FilterHelper.buildQueryParams(params.filters)),
        ...(params.query && { query: params.query }),
        ...(params.sort && { 'sort[]': params.sort }),
      },
    });
  }

  public searchDrivers(
    query: string,
    params: Omit<OnyxPagination & DriversFilters, 'query'>,
    options?: {
      showDriverCard?: boolean;
    },
  ): Observable<OnyxDropdownOptionsSourceResult<Driver>> {
    return this.reload$.pipe(
      startWith(undefined),
      switchMap(() => this.listDrivers({ ...params, query })),
      map((response) => ({
        options: response.items.map((driver): OnyxOption<Driver> => ({
          name: this.namePipe.transform(driver.driverData),
          value: driver,
          avatar: driver.driverData,
          ...(driver.unavailability && {
            rightIcon: {
              name: `${driver.unavailability.reason}-outlined`,
              size: 20,
            },
            rightIconTooltip: {
              ref: IconTextTooltipComponent,
              inputs: {
                icon: {
                  name: `${driver.unavailability.reason}-outlined`,
                  size: 20,
                },
                heading: `${DictionaryCode.DRIVER_UNAVAILABILITY_REASON}.${driver.unavailability.reason}`,
                description: this.datePipe.transform(
                  driver.unavailability.dateRange.from,
                  'date-dot',
                  { endDate: driver.unavailability.dateRange.to },
                ),
              } satisfies OnyxComponentInputs<IconTextTooltipComponent>,
            },
          }),
          ...(options?.showDriverCard && {
            description: driver.driverCard?.number ?? '-',
          }),
        })),
        totalItems: response.totalItems,
      })),
    );
  }

  public checkPrivatePhone(
    phone: OnyxPhone,
  ): Observable<
    { conflict: false } | { conflict: true; driverUuid: string; local: boolean }
  > {
    return this.get<{ driverUuid: string; local: boolean }>('/drivers/check', {
      params: {
        areaCode: phone.areaCode,
        phoneNumber: phone.number,
        companyId: this.authStore.companyId()!,
      },
    }).pipe(
      map(({ driverUuid, local }) => ({
        conflict: true,
        driverUuid,
        local,
      })),
      catchError((response: OnyxHttpErrorResponse) => {
        if (response.status === HttpStatusCode.NotFound) {
          return of({ conflict: false } as const);
        }
        throw response;
      }),
    );
  }

  public getDriver(uuid: string): Observable<Driver> {
    return this.get(`/drivers/${uuid}`);
  }

  public addDriver(dto: DriverFormDto): Observable<Driver> {
    return this.uploadFiles(dto).pipe(
      switchMap((dto) => this.post<Driver>('/drivers', dto)),
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this._reload$.next()),
    );
  }

  public importDrivers(data: DriversImportData[]): Observable<ImportProgress> {
    const dto = data.map(
      (item) => omit(item, 'index', 'base') as DriverFormDto,
    );

    return from(dto).pipe(
      concatMap((dto, index) =>
        this.addDriver(dto).pipe(
          map(() => ({ ok: true, index })),
          defaultIfEmpty({ ok: false, index }),
          catchError((response) => {
            ValidationHelper.handleUnexpectedResponse(response, this.injector);
            return of({ ok: false, index });
          }),
        ),
      ),
    );
  }

  public editDriver(uuid: string, dto: DriverFormDto): Observable<void> {
    return this.uploadFiles(dto).pipe(
      switchMap((dto) => this.put<void>(`/drivers/${uuid}`, dto)),
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this.reloadRelated()),
    );
  }

  public archiveDriver(uuid: string): Observable<void> {
    return this.post<void>(`/drivers/${uuid}/archive`, undefined).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public unarchiveDriver(uuid: string): Observable<void> {
    return this.post<void>(`/drivers/${uuid}/unarchive`, undefined).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public batchDrivers(
    driversUuid: string[],
    setFields: {
      archive?: boolean;
      unarchive?: boolean;
    },
  ): Observable<void> {
    return this.put<void>('/drivers/batch', { driversUuid, setFields }).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public getDriverUnavailabilities(
    driverUuid: string,
    params: OnyxPagination & { past: boolean },
  ): Observable<OnyxPaginated<Unavailability>> {
    return this.get(`/drivers/${driverUuid}/unavailabilities`, {
      params: {
        page: params.page,
        limit: params.limit,
        past: params.past,
      },
    });
  }

  public addDriverUnavailability(
    driverUuid: string,
    form: UnavailabilityForm,
  ): Observable<void> {
    return this.uploadUnavailabilityFile(form).pipe(
      switchMap((form) =>
        this.post<void>(`/drivers/${driverUuid}/unavailabilities`, form),
      ),
      tap(() => this.reloadRelated()),
    );
  }

  public editDriverUnavailability(
    driverUuid: string,
    uuid: string,
    form: UnavailabilityForm,
  ): Observable<void> {
    return this.uploadUnavailabilityFile(form).pipe(
      switchMap((form) =>
        this.put<void>(`/drivers/${driverUuid}/unavailabilities/${uuid}`, form),
      ),
      tap(() => this.reloadRelated()),
    );
  }

  public deleteDriverUnavailability(
    driverUuid: string,
    uuid: string,
  ): Observable<void> {
    return this.delete<void>(
      `/drivers/${driverUuid}/unavailabilities/${uuid}`,
    ).pipe(tap(() => this.reloadRelated()));
  }

  private uploadFiles(dto: DriverFormDto): Observable<DriverFormDto> {
    return of(dto).pipe(
      map((dto): BatchFileUpload[] => [
        {
          path: 'employmentConditions.scans',
          files: dto.employmentConditions.scans,
        },
        ...(dto.driverCard
          ? [
              {
                path: 'driverCard.scans',
                files: dto.driverCard.scans,
              },
            ]
          : []),
        {
          path: 'driversLicenseAndProfessionalQualifications.scans',
          files: dto.driversLicenseAndProfessionalQualifications.scans,
        },
        ...(dto.identityDocuments.passport
          ? [
              {
                path: 'identityDocuments.passport.scans',
                files: dto.identityDocuments.passport.scans,
              },
            ]
          : []),
        ...(dto.identityDocuments.identityDocument
          ? [
              {
                path: 'identityDocuments.identityDocument.scans',
                files: dto.identityDocuments.identityDocument.scans,
              },
            ]
          : []),
        ...(dto.identityDocuments.residenceCard
          ? [
              {
                path: 'identityDocuments.residenceCard.scans',
                files: dto.identityDocuments.residenceCard.scans,
              },
            ]
          : []),
        ...(dto.identityDocuments.polishCard
          ? [
              {
                path: 'identityDocuments.polishCard.scans',
                files: dto.identityDocuments.polishCard.scans,
              },
            ]
          : []),
        {
          path: 'clearCriminalRecordCertificate.scans',
          files: dto.clearCriminalRecordCertificate.scans,
        },
        ...dto.visas.map((visa, index) => ({
          path: `visas[${index}].scans`,
          files: visa.scans,
        })),
        ...dto.permissions.map((permission, index) => ({
          path: `permissions[${index}].scans`,
          files: permission.scans,
        })),
      ]),
      switchMap((data) => this.storageService.uploadBatch(data)),
      map((data) => this.storageService.mergeBatch(dto, data)),
    );
  }

  private uploadUnavailabilityFile(
    form: UnavailabilityForm,
  ): Observable<UnavailabilityForm> {
    return of(form).pipe(
      map((form): BatchFileUpload[] => [
        {
          path: 'file',
          files: form.file,
        },
      ]),
      switchMap((data) => this.storageService.uploadBatch(data)),
      map((data) => this.storageService.mergeBatch(form, data)),
    );
  }

  private handleConflictErrors(
    dto: DriverFormDto,
    response: OnyxHttpErrorResponse,
  ): Observable<never> {
    if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

    const I18N = 'drivers.driverForm';
    const conflictFields = [
      {
        key: 'driverData.peselNumber',
        message: `${I18N}.uniqueErrors.peselNumber`,
        params: { peselNumber: dto.driverData?.peselNumber },
      },
      {
        key: 'driverData.privatePhone',
        message: `${I18N}.uniqueErrors.privatePhone`,
        params: {
          privatePhone: this.phonePipe.transform(dto.driverData.privatePhone!),
        },
      },
      {
        key: 'driverData.workPhone',
        message: `${I18N}.uniqueErrors.workPhone`,
        params: {
          workPhone: this.phonePipe.transform(dto.driverData.workPhone!),
        },
      },
      {
        key: 'driversLicense.driversLicenseNumber',
        message: `${I18N}.uniqueErrors.driversLicense`,
        params: {
          driversLicense:
            dto.driversLicenseAndProfessionalQualifications
              ?.driversLicenseNumber,
        },
      },
      {
        key: 'driverCard.number',
        message: `${I18N}.uniqueErrors.driverCard`,
        params: { driverCard: dto.driverCard?.number },
      },
      {
        key: 'identityDocuments.passport.number',
        message: `${I18N}.uniqueErrors.passport`,
        params: { passport: dto.identityDocuments?.passport?.number },
      },
      {
        key: 'identityDocuments.identityDocument.number',
        message: `${I18N}.uniqueErrors.identityDocument`,
        params: {
          identityDocument: dto.identityDocuments?.identityDocument?.number,
        },
      },
    ];

    this.conflictHelper.handleConflictErrors(response, conflictFields);

    throw response;
  }

  private reloadRelated(): void {
    this._reload$.next();
    this.injector.get(FleetService)['_reload$'].next();
    this.injector.get(DelegationsService)['_reload$'].next();
  }
}
